import type { Journey, PixelConfig, Touchpoint, AttributionModel } from './types';
import { get, set, StorageKeys } from './storage';
import { isSafeString, log, safeJsonParse } from './utils';

const EMPTY_JOURNEY: Journey = { touchpoints: [], firstSeen: 0, lastSeen: 0 };

function pageId(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin + window.location.pathname;
}

export function loadJourney(): Journey {
  const raw = get(StorageKeys.journey);
  const parsed = safeJsonParse<Journey>(raw, EMPTY_JOURNEY);
  // Validate shape
  if (!Array.isArray(parsed.touchpoints)) return { ...EMPTY_JOURNEY };
  return parsed;
}

export function saveJourney(journey: Journey): boolean {
  return set(StorageKeys.journey, JSON.stringify(journey));
}

/**
 * Add a touchpoint with cooldown + max-length enforcement.
 * Returns the resulting Journey (saved to storage).
 */
export function addTouchpoint(
  source: string,
  config: PixelConfig,
  now: number = Date.now()
): Journey {
  if (!isSafeString(source)) {
    log('rejected unsafe source', source);
    return loadJourney();
  }

  const journey = loadJourney();

  // Cooldown: skip if THIS source appears anywhere in the journey within
  // the cooldown window. The earlier "last touchpoint only" check let
  // A → B → A patterns add the second A on a quick back-and-forth, which
  // produced duplicate entries like `email_q4 | email_q4 | TDG_Direct`
  // when a user refreshed or re-landed within the hour.
  const cooldownCutoff = now - config.touchpointCooldownMs;
  const recentDupe = journey.touchpoints.some(
    (t) => t.source === source && t.ts >= cooldownCutoff
  );
  if (recentDupe) {
    log('cooldown active for', source);
    return journey;
  }

  // Validity: drop touchpoints older than validityDays
  const cutoff = now - config.validityDays * 86_400_000;
  const fresh = journey.touchpoints.filter((t) => t.ts >= cutoff);

  fresh.push({ source, ts: now, page: pageId() });

  // Cap length
  while (fresh.length > config.maxJourneyLength) fresh.shift();

  const next: Journey = {
    touchpoints: fresh,
    firstSeen: fresh[0]?.ts ?? now,
    lastSeen: fresh[fresh.length - 1]?.ts ?? now,
  };
  saveJourney(next);
  log('journey updated', next.touchpoints.map((t) => t.source));
  return next;
}

/** Merge an externally-provided journey (e.g. from link decoration). */
export function mergeJourney(incoming: Journey, config: PixelConfig): Journey {
  if (!incoming?.touchpoints?.length) return loadJourney();
  const existing = loadJourney();
  const combined: Touchpoint[] = [...existing.touchpoints, ...incoming.touchpoints]
    // Filter unsafe entries
    .filter((t) => t && typeof t.source === 'string' && isSafeString(t.source))
    // Sort by timestamp
    .sort((a, b) => a.ts - b.ts);

  // Dedupe runs of same source within cooldown
  const deduped: Touchpoint[] = [];
  for (const t of combined) {
    const last = deduped[deduped.length - 1];
    if (last && last.source === t.source && t.ts - last.ts < config.touchpointCooldownMs) {
      continue;
    }
    deduped.push(t);
  }

  // Cap length, dropping oldest
  while (deduped.length > config.maxJourneyLength) deduped.shift();

  const merged: Journey = {
    touchpoints: deduped,
    firstSeen: deduped[0]?.ts ?? Date.now(),
    lastSeen: deduped[deduped.length - 1]?.ts ?? Date.now(),
  };
  saveJourney(merged);
  return merged;
}

export function getAttribution(journey: Journey, model: AttributionModel): string {
  const tps = journey.touchpoints;
  if (tps.length === 0) return '';
  switch (model) {
    case 'first_touch':
      return tps[0].source;
    case 'last_touch':
      return tps[tps.length - 1].source;
    case 'linear':
      // Linear is for reporting — for a single value, fall back to last_touch
      return tps[tps.length - 1].source;
  }
}

export function getJourneyString(journey: Journey): string {
  // Pipe separator — Formstack's WAF treats `>` as a potential XSS signature
  // (it appears in `<script>`, `<img>` etc), and multiple `>` chars in a
  // single value increases the rejection probability. Pipe is URL-safe,
  // visually clear, and never flagged.
  return journey.touchpoints.map((t) => t.source).join(' | ');
}

export function getTimestampString(journey: Journey): string {
  // No parentheses (WAF flags `(` + `:` as SQL-injection signature) and
  // pipe separator (same reasoning as getJourneyString re: `>`).
  // Format: "YYYY-MM-DD HH:MM | YYYY-MM-DD HH:MM | ..."
  return journey.touchpoints
    .map((t) => {
      const d = new Date(t.ts);
      const date = d.toISOString().split('T')[0];
      const time = d.toTimeString().split(' ')[0].slice(0, 5);
      return `${date} ${time}`;
    })
    .join(' | ');
}
